import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Button, Divider, Input, Form, Radio, Checkbox, Space,
	Card,
} from 'antd-mobile';
import { message, Select } from 'antd';
import { CaretDownFilled } from '@ant-design/icons';
import { useAppStore, useQuizStore, useSessionStore } from '../../store';

import { logger } from '../../libs/utils/logger';
import { APP_CONFIG, QuizUserDetailsInputType } from '../../config';

const appStoreSelector = (state: AppState) => ({
	setAppLoading: state.setAppLoading,
});

const sessionStoreSelector = (state: SessionState) => ({
	tryTest: state.sessionData?.tryTest,
	sessionData: state.sessionData,
});

const quizStoreSelector = (state: QuizState) => ({
	quizData: state.quizData,
	startQuiz: state.startQuiz,
	serverTime: state.serverTime,
});

const inputStyle = {
	border: 'none',
	borderBottom: '1px solid #d9d9d9',
	borderRadius: 0,
};
interface QuizDetailsScreenProps {
	quizName: string
}

export const QuizDetailsScreen: React.FunctionComponent<QuizDetailsScreenProps> = () => {
	const { setAppLoading } = useAppStore(appStoreSelector);
	const { quizData, startQuiz, serverTime } = useQuizStore(quizStoreSelector);
	const { tryTest, sessionData } = useSessionStore(sessionStoreSelector);
	const [startingQuiz, setStartingQuiz] = useState<boolean>(false);
	const [isFieldsPresent, setIdFieldPresent] = useState<boolean>(false);

	const [form] = Form.useForm();

	const history = useHistory();

	const logoutUser = useCallback(() => {
		try {
			if (!navigator.onLine) {
				throw new Error('Please check your internet connection.');
			}
			history.push({
				state: { referrer: '' },
			});
			window.location.replace(`${APP_CONFIG.QuizServerURL}/logout`);
		} catch (error: any) {
			message.error(error.message);
		}
	}, [history]);

	useEffect(() => {
		if (Object.hasOwn(document, 'visibilityState')) {
			message.error(' extension detected');
			setTimeout(logoutUser, 2000);
		}
	}, [sessionData?.userId, logoutUser]);

	// eslint-disable-next-line consistent-return
	const handleStartQuiz = useCallback(async () => {
		try {
			if (!tryTest
				&& (
					quizData?.endTime
					&& (serverTime ?? Date.now()) > new Date(quizData?.endTime).getTime())
			) {
				return logoutUser();
			}
			// setAppLoading(true, 'starting test...');
			setAppLoading(true);
			setStartingQuiz(true);

			await startQuiz();
		} catch (e: any) {
			logger.error(e);

			setStartingQuiz(false);
			message.error(e.message);
		} finally {
			setAppLoading(false);
		}
	}, [tryTest, quizData?.endTime, serverTime, setAppLoading, startQuiz, logoutUser]);

	// eslint-disable-next-line consistent-return
	const handleUserQuizDetailsFormSubmit = useCallback(async (values: any) => {
		try {
			const data = Object.entries(values);

			const quizUserDetails = data.map((el) => {
				const [key, value] = el;

				const detailData = quizData?.quizUserDetails?.find((detail) => detail.fieldName === key);

				return {
					fieldLabel: detailData?.fieldLabel,
					fieldValue: value instanceof Array ? value.join(', ') : value,
				};
			});

			// setAppLoading(true, 'starting test...');
			if (!tryTest
				&& (
					quizData?.endTime
					&& (serverTime ?? Date.now()) > new Date(quizData?.endTime).getTime())
			) {
				return logoutUser();
			}

			setAppLoading(true);
			setStartingQuiz(true);

			await startQuiz(quizUserDetails);
		} catch (e: any) {
			logger.error(e);

			setStartingQuiz(false);
			message.error(e.message);
		} finally {
			setAppLoading(false);
		}
	}, [
		tryTest,
		quizData?.endTime,
		quizData?.quizUserDetails,
		serverTime, setAppLoading,
		startQuiz,
		logoutUser,
	]);

	useEffect(() => {
		let isFieldP = false;
		quizData?.quizUserDetails?.forEach((el) => {
			if (el.fieldIsSelected) {
				isFieldP = true;
			}
		});
		setIdFieldPresent(isFieldP);
	}, [quizData?.quizUserDetails]);

	return (
		<div style={{ background: '#f8f8f8', overflowY: 'auto' }}>
			<div
				style={{
					background: '#fff',
					display: 'flex',
					gap: 24,
					padding: '12px 20px',
					borderTop: '1px solid #f0f0f0',
				}}
			>
				<span style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
					Total Questions
					<span style={{ color: 'var(--primary-color)', marginLeft: 8 }}>{quizData?.questions}</span>
				</span>
				<span style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
					Total Time
					<span style={{ color: 'var(--primary-color)', marginLeft: 8 }}>
						{quizData?.quizTime}
						{' '}
						mins
					</span>
				</span>
			</div>

			{
				quizData?.instructions
				&& quizData.instructions !== '<p><br></p>'
				&& quizData.instructions.replace(/^<p>|<\/p>$/g, '').trim()
				&& (
					<div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
						<Divider style={{ margin: 0 }} />
						<div style={{ background: '#fff', padding: '20px' }}>
							<span
								style={{
									display: 'block',
									fontSize: 18,
									fontWeight: 800,
									color: '#333',
									marginBottom: 12,
								}}
							>
								Instructions
							</span>
							<div
								className="quiz-instructions ql-editor"
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: quizData.instructions as string,
								}}
							/>
						</div>
					</div>
				)
			}

			{/* Form or Start Button */}
			{
				quizData?.quizUserDetails && quizData.quizUserDetails.length && isFieldsPresent
					? (
						<div style={{ background: '#fff', padding: '0 20px 20px' }}>
							<div style={{ marginBottom: 8, color: '#999', fontSize: 12 }}>
								<span>
									Enter Your Details
									{' '}
								</span>
								<span>(*required)</span>
							</div>
							<Card style={{ border: '1px solid #ccc' }}>
								<Form
									form={form}
									layout="vertical"
									onFinish={handleUserQuizDetailsFormSubmit}
									style={{
										'--border-top': 'none',
										'--border-inner': 'none',
										'--border-bottom': 'none',
									}}
									footer={(
										<div style={{ display: 'flex', justifyContent: 'center' }}>
											<Button
												color="primary"
												loading={startingQuiz}
												onClick={form.submit}
												shape="rounded"
												size="large"
												style={{ width: 200, fontWeight: 700, fontSize: 16 }}
											>
												start test
											</Button>
										</div>
									)}
								>
									{
										quizData.quizUserDetails?.map((el) => (
											el?.fieldIsSelected
											&& (
												<Form.Item
													key={el._id}
													label={`${el.fieldLabel} *`}
													name={el.fieldName}
													rules={[{
														required: true,
														whitespace: true,
														message: (() => {
															if (el.fieldType === QuizUserDetailsInputType.Select) {
																return `Please select your ${el.fieldLabel}!`;
															} if (el.fieldType === QuizUserDetailsInputType.Checkbox) {
																return `Please select value(s) for ${el.fieldLabel}!`;
															} if (el.fieldType === QuizUserDetailsInputType.Radio) {
																return `Please select a value for ${el.fieldLabel}!`;
															}

															return `Please enter your ${el.fieldLabel}!`;
														})(),
														transform: el.fieldType === QuizUserDetailsInputType.Checkbox
															? (val: string[]) => val?.join(',')
															: undefined,
													},
													{
														pattern: new RegExp(/^[a-z0-9&/.() @,_-]+$/i),
														message: el.fieldType === QuizUserDetailsInputType.TextInput ? 'Only alphanumeric and special character(&/.() @,_-) allowed' : '',
													},
													]}
												>
													{
														(() => {
															if (el.fieldType === QuizUserDetailsInputType.TextInput) {
																return <Input style={inputStyle} />;
															}

															if (el.fieldType === QuizUserDetailsInputType.Select) {
																return (
																	<Select
																		suffixIcon={<CaretDownFilled style={{ color: '#818181' }} />}
																		options={el.fieldOptions?.map((option) => ({
																			label: option, value: option,
																		}))}
																		placeholder="Select a value..."
																		getPopupContainer={(trigger) => trigger.parentElement}
																	/>
																);
															}

															if (el.fieldType === QuizUserDetailsInputType.Checkbox) {
																return (
																	<Checkbox.Group>
																		<Space direction="vertical">
																			{el.fieldOptions?.map((option) => (
																				<Checkbox key={option} value={option}>
																					{option}
																				</Checkbox>
																			))}
																		</Space>
																	</Checkbox.Group>
																);
															}

															if (el.fieldType === QuizUserDetailsInputType.Radio) {
																return (
																	<Radio.Group>
																		<Space direction="vertical">
																			{el.fieldOptions?.map((option) => (
																				<Radio key={option} value={option}>
																					{option}
																				</Radio>
																			))}
																		</Space>
																	</Radio.Group>
																);
															}

															return null;
														})()
													}
												</Form.Item>
											)
										))
									}
								</Form>
							</Card>
						</div>
					) : (
						<div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
							<Button
								color="primary"
								loading={startingQuiz}
								onClick={handleStartQuiz}
								shape="rounded"
								size="large"
								style={{ width: 200, fontWeight: 700, fontSize: 16 }}
							>
								start test
							</Button>
						</div>
					)
			}

			{/* Disclaimer */}
			{
				quizData?.isWebCamAllowed
				&& (
					<div style={{ padding: '20px' }}>
						<span
							style={{
								display: 'block',
								fontSize: 18,
								fontWeight: 800,
								color: '#333',
								marginBottom: 12,
							}}
						>
							Disclaimer
						</span>
						<p
							style={{
								fontSize: 13, color: '#888', lineHeight: 1.5, margin: 0,
							}}
						>
							By using this system, you acknowledge that your video is being actively
							monitored and recorded throughout the session.
							Please ensure you remain in a presentable and appropriate state at all times.
							Your compliance is necessary to maintain the integrity of the process.
						</p>
					</div>
				)
			}
		</div>
	);
};
